import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { useDeleteMessengerIntegrationMutation } from "modules/settings/hooks/messenger/useDeleteMessengerIntegrationMutation"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { MessengerIntegrationsResponseData } from "schema"
import { z } from "zod"

type MessengerIntegration = z.infer<typeof MessengerIntegrationsResponseData>[number]

type Props = {
  integration: MessengerIntegration | null
  onOpenChange: (isOpen: boolean) => void
}

export const DeleteMessengerIntegration = ({ integration, onOpenChange }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync } = useDeleteMessengerIntegrationMutation()
  const [isLoading, setIsLoading] = useState(false)

  const onCancel = () => {
    onOpenChange(false)
  }

  const onDelete = async () => {
    if (!integration) return

    setIsLoading(true)

    await mutateAsync({ id: integration.id })
      .then(() => {
        setIsLoading(false)
        onOpenChange(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  return (
    <Dialog open={integration !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.messenger.deleteIntegration.title")}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey={"settings.integrations.messenger.deleteIntegration.description"}
              values={{ name: integration?.name }}
              components={{ b: <b /> }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <DialogFooter>
            <Button variant={"secondary"} onClick={onCancel} disabled={isLoading}>
              {t("settings.integrations.messenger.deleteIntegration.actions.cancel")}
            </Button>
            <Button onClick={onDelete} variant="destructive" disabled={isLoading}>
              {t("settings.integrations.messenger.deleteIntegration.actions.delete")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
